import { z } from "zod";
import { optionalEnum, optionalUuid } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

export const invoiceStatuses = [
  "draft",
  "sent",
  "paid",
  "partial",
  "overdue",
  "cancelled",
  "refunded",
] as const;

export const invoiceTypes = [
  "deposit",
  "progress",
  "final",
  "service",
] as const;

// AR aging buckets — keep in sync with finance.invoice_aging_bucket() in
// supabase/migrations/20260506234231_adv_788_invoice_aging_summary.sql.
const invoiceAgingBuckets = [
  "current",
  "days_31_60",
  "days_61_90",
  "days_90_plus",
] as const;
export type InvoiceAgingBucket = (typeof invoiceAgingBuckets)[number];

export const InvoiceAgingBucketSchema = z
  .enum(invoiceAgingBuckets)
  .describe("Aging Bucket");

export const InvoiceAgingSummaryRowSchema = z.object({
  bucket: InvoiceAgingBucketSchema,
  invoice_count: z.number().int().nonnegative().describe("Invoice Count"),
  total_balance: z.number().nonnegative().describe("Total Balance"),
});

export const InvoiceSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  opportunity_id: z.string().uuid().nullable().describe("Opportunity"),
  job_id: z.string().uuid().nullable().describe("Job"),
  customer_role_id: z.string().uuid().nullable().describe("Customer"),
  invoice_number: z.string().describe("Invoice Number"),
  invoice_type: z.enum(invoiceTypes).nullable().describe("Invoice Type"),
  status: z.enum(invoiceStatuses).describe("Status"),
  subtotal: z.number().nullable().describe("Subtotal"),
  tax_amount: z.number().nullable().describe("Tax Amount"),
  total_amount: z.number().nullable().describe("Total Amount"),
  amount_paid: z.number().nullable().describe("Amount Paid"),
  balance_due: z.number().nullable().optional().describe("Balance Due"),
  discount: z.number().nullable().describe("Discount"),
  issue_date: z.string().nullable().describe("Issue Date"),
  due_date: z.string().nullable().describe("Due Date"),
  sent_at: z.string().nullable().describe("Sent At"),
  paid_at: z.string().nullable().describe("Paid At"),
  payment_method: z.string().nullable().describe("Payment Method"),
  check_number: z.string().nullable().describe("Check Number"),
  notes: z.string().nullable().describe("Notes"),
  custom_fields: z.record(z.unknown()).default({}).describe("Custom Fields"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
  assigned_rep_name: z.string().nullable().optional().describe("Sales Rep"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

// IMPORT SCHEMA GUARD (ADV-1257): total_amount is DB-computed
// (finance.invoices.total_amount GENERATED ALWAYS AS subtotal - discount +
// tax_amount) and is NEVER accepted as input. subtotal / tax_amount / discount
// stay accepted: they are raw stored values with no line-item source in the DB
// (external sources such as the Jobber adapter supply them).
export const InvoiceInsertSchema = z.object({
  opportunity_id: optionalUuid().describe("Opportunity"),
  job_id: optionalUuid().describe("Job"),
  customer_role_id: z.string().uuid().describe("Customer"),
  invoice_number: z.string().describe("Invoice Number"),
  invoice_type: optionalEnum(invoiceTypes).describe("Invoice Type"),
  status: z.enum(invoiceStatuses).default("draft").describe("Status"),
  subtotal: z.number().optional().describe("Subtotal"),
  tax_amount: z.number().optional().describe("Tax Amount"),
  issue_date: z.string().optional().describe("Issue Date"),
  due_date: z.string().optional().describe("Due Date"),
  discount: z.number().optional().describe("Discount"),
  notes: z.string().optional().describe("Notes"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
});

export const InvoiceUpdateSchema = InvoiceInsertSchema.partial().extend({
  amount_paid: z.number().optional().describe("Amount Paid"),
  sent_at: z.string().optional().describe("Sent At"),
  paid_at: z.string().optional().describe("Paid At"),
  payment_method: z.string().optional().describe("Payment Method"),
  check_number: z.string().optional().describe("Check Number"),
});

export const InvoiceQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  customer_role_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  deal_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  job_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  status: z.enum(invoiceStatuses).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  invoice_type: z.enum(invoiceTypes).optional(),
  date_from: z
    .string()
    .optional()
    .describe("Filter issue_date >= this date (YYYY-MM-DD)"),
  date_to: z
    .string()
    .optional()
    .describe("Filter issue_date <= this date (YYYY-MM-DD)"),
  aging_bucket: InvoiceAgingBucketSchema.optional().describe(
    "Filter by AR aging bucket (only invoices with balance_due > 0 and status in [sent, partial, overdue])",
  ),
  search: z.string().optional().describe("Search invoice_number"),
});
